<script setup lang="ts">
import { useTitle } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { watch } from 'vue'
import { RouterView, useRouter } from 'vue-router'
import { Toaster } from 'vue-sonner'

import { client } from './client/client.gen'
import CookieConsent from './components/CookieConsent.vue'
import DesktopAvatarNotice from './components/DesktopAvatarNotice.vue'
import { clearCreateDraftAnalytics } from './composables/preprocess/draftStore'
import { useCookieConsent } from './composables/useCookieConsent'
import { useRegionStore } from './stores/region'
import { useUserStore } from './stores/user'
import { fetchWithCredentials } from './utils/fetch'

const router = useRouter()
const userStore = useUserStore()
const { isAuthenticated, authChecked } = storeToRefs(userStore)
const regionStore = useRegionStore()
const { hasAccepted } = useCookieConsent()

// Also clear saved analytics when the create page is not mounted.
watch(hasAccepted, (accepted) => {
  if (!accepted)
    clearCreateDraftAnalytics()
}, { immediate: true, flush: 'sync' })

watch(isAuthenticated, (isAuth, wasAuth) => {
  // Skip the transient flip from undefined→false during bootstrap; only
  // react once we've confirmed the cookie is gone.
  if (!authChecked.value)
    return
  // Redirect only — the "session expired" toast is emitted by the user
  // store's handleUnauthorized(), so a deliberate logout doesn't trigger it.
  // The public invitation page supports signing out before creating a new account.
  if (wasAuth && !isAuth && router.currentRoute.value.path !== '/invite')
    router.push('/login')
})

const title = useTitle()
watch(() => regionStore.currentRegion, () => {
  client.setConfig({
    baseUrl: regionStore.currentRegion.apiBaseUrl,
    fetch: fetchWithCredentials,
  })

  title.value = 'Spatius Studio'
}, { immediate: true })
</script>

<template>
  <div class="min-h-screen bg-[#FFFFFF] transition-all duration-300 ease-in-out dark:bg-[#282828]">
    <Toaster position="top-center" :expand="true" :rich-colors="true" :duration="3000" />
    <RouterView />
    <DesktopAvatarNotice />
    <CookieConsent />
  </div>
</template>
